import { useCallback, useEffect, useMemo, useState } from 'react'
import type { PostgrestError } from '@supabase/supabase-js'
import { Mail, Phone, RefreshCw, Search, Sparkles, UserRound } from 'lucide-react'
import {
  ADMIN_BOOKING_SELECT,
  adminBookingHref,
  asAdminBookings,
  bookingListStatus,
  bookingPackage,
  bookingsForCustomer,
  bookingVehicleLabel,
  customerProtectionUsed,
  customerVehicles,
  formatAppointment,
  lastBooking,
  nextUpcomingBooking,
  paidDepositCents,
  type AdminCanonicalBooking,
  type AdminCustomerRow,
} from '../../lib/admin-workspace'
import { supabase } from '../../lib/supabase'
import { AdminSidebar } from '../admin/admin-sidebar'
import { PageTitle } from '../seo/page-title'

interface CustomerConsent {
  marketing_email_opt_in: boolean | null
  unsubscribed: boolean | null
  consent_timestamp: string | null
}

interface CustomerRow extends AdminCustomerRow {
  tags: string[] | null
  customer_consents: CustomerConsent | CustomerConsent[] | null
}

type LoadStatus = 'loading' | 'ready' | 'error'

function getConsent(customer: CustomerRow) {
  return Array.isArray(customer.customer_consents) ? customer.customer_consents[0] : customer.customer_consents
}

function formatDate(value: string | null | undefined) {
  if (!value) return 'No booking yet'
  return new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value))
}

function isContactable(customer: CustomerRow) {
  const consent = getConsent(customer)
  return Boolean(customer.email) && consent?.marketing_email_opt_in === true && consent.unsubscribed !== true
}

function depositSummary(bookings: AdminCanonicalBooking[]) {
  const paid = bookings.reduce((total, booking) => total + paidDepositCents(booking), 0)
  const count = bookings.filter((booking) => paidDepositCents(booking) > 0).length
  return `${new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(paid / 100)} across ${count} paid deposit${count === 1 ? '' : 's'}`
}

export function AdminCustomers() {
  const [customers, setCustomers] = useState<CustomerRow[]>([])
  const [bookings, setBookings] = useState<AdminCanonicalBooking[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [message, setMessage] = useState<string | null>(null)

  const loadCustomers = useCallback(async () => {
    setStatus('loading')
    setMessage(null)

    try {
      const [customerResult, bookingResult] = await Promise.all([
        supabase
          .from('customers')
          .select('id, created_at, full_name, first_name, last_name, email, phone, tags, source, last_booking_at, total_bookings, customer_consents(marketing_email_opt_in, unsubscribed, consent_timestamp)')
          .order('last_booking_at', { ascending: false, nullsFirst: false })
          .limit(300),
        supabase
          .from('bookings')
          .select(ADMIN_BOOKING_SELECT)
          .order('created_at', { ascending: false })
          .limit(500),
      ])

      if (customerResult.error) throw customerResult.error
      if (bookingResult.error) throw bookingResult.error

      const customerRows = (customerResult.data ?? []) as CustomerRow[]
      setCustomers(customerRows)
      setBookings(asAdminBookings(bookingResult.data))
      setSelectedId((current) => current ?? customerRows[0]?.id ?? null)
      setStatus('ready')
    } catch (error) {
      const customerError = error as PostgrestError
      setMessage(customerError.message || 'Unable to load customers from Supabase.')
      setStatus('error')
    }
  }, [])

  useEffect(() => {
    void loadCustomers()
  }, [loadCustomers])

  const bookingsByCustomer = useMemo(() => {
    const map = new Map<string, AdminCanonicalBooking[]>()
    customers.forEach((customer) => {
      map.set(customer.id, bookingsForCustomer(customer, bookings))
    })
    return map
  }, [bookings, customers])

  const filteredCustomers = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return customers

    return customers.filter((customer) => {
      const customerBookings = bookingsByCustomer.get(customer.id) ?? []
      return [
        customer.full_name,
        customer.email,
        customer.phone,
        ...customerVehicles(customerBookings),
        ...customerProtectionUsed(customerBookings),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    })
  }, [bookingsByCustomer, customers, query])

  const selectedCustomer = customers.find((customer) => customer.id === selectedId) ?? filteredCustomers[0] ?? null
  const selectedBookings = selectedCustomer ? bookingsByCustomer.get(selectedCustomer.id) ?? [] : []
  const contactableCount = customers.filter(isContactable).length
  const repeatCustomerCount = customers.filter((customer) => (customer.total_bookings ?? bookingsByCustomer.get(customer.id)?.length ?? 0) > 1).length
  const nextBooking = nextUpcomingBooking(selectedBookings)
  const previousBooking = lastBooking(selectedBookings)

  return (
    <div className="admin-dashboard">
      <PageTitle title="Customers | AUTOGLOW Detailing Admin" />
      <AdminSidebar active="customers" />
      <main>
        <header className="admin-topbar">
          <div>
            <p className="eyebrow">Client Database</p>
            <h1>Customers</h1>
          </div>
          <div className="admin-topbar-actions">
            <button type="button" onClick={() => void loadCustomers()} disabled={status === 'loading'}>
              <RefreshCw size={17} /> {status === 'loading' ? 'Refreshing' : 'Refresh'}
            </button>
          </div>
        </header>

        {message ? <p className={status === 'error' ? 'admin-alert error' : 'admin-alert'}>{message}</p> : null}

        <section className="booking-ops-grid customers-ops-grid">
          <article className="booking-stat-panel">
            <span>Total Customers</span>
            <strong>{customers.length}</strong>
            <p>Derived from ceramic booking identity</p>
          </article>
          <article className="booking-stat-panel growth">
            <span>Re-engageable</span>
            <strong>{contactableCount}</strong>
            <p>Email opt-in and not unsubscribed</p>
          </article>
          <article className="booking-stat-panel">
            <span>Repeat Clients</span>
            <strong>{repeatCustomerCount}</strong>
            <p>More than one booking</p>
          </article>
          <article className="booking-stat-panel warning">
            <span>Booking Rows</span>
            <strong>{bookings.length}</strong>
            <p>Canonical bookings linked by email</p>
          </article>
        </section>

        <section className="customer-workspace-grid">
          <article className="admin-card customer-list-card">
            <div className="customer-search">
              <Search size={16} />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search name, email, phone, vehicle or protection" />
            </div>
            {status === 'loading' ? <p className="admin-empty-state">Loading customer records from Supabase...</p> : null}
            {status !== 'loading' && filteredCustomers.length === 0 ? <p className="admin-empty-state">No customers match that search.</p> : null}
            <div className="customer-list">
              {filteredCustomers.map((customer) => {
                const customerBookings = bookingsByCustomer.get(customer.id) ?? []
                const latest = lastBooking(customerBookings)
                return (
                  <button className={selectedCustomer?.id === customer.id ? 'active' : undefined} type="button" key={customer.id} onClick={() => setSelectedId(customer.id)}>
                    <span><UserRound size={16} /> {customer.full_name}</span>
                    <small>{latest ? `${bookingVehicleLabel(latest)} / ${bookingPackage(latest).displayName}` : customer.email || 'No booking history'}</small>
                    <em>{customerBookings.length} booking{customerBookings.length === 1 ? '' : 's'}</em>
                  </button>
                )
              })}
            </div>
          </article>

          <article className="admin-card customer-detail-card">
            {selectedCustomer ? (
              <>
                <div className="customer-profile-header">
                  <div>
                    <span className="status-pill">{isContactable(selectedCustomer) ? 'email opted in' : 'manual contact'}</span>
                    <h2>{selectedCustomer.full_name}</h2>
                    <p>{selectedCustomer.email || 'No email stored'} / {selectedCustomer.phone || 'No phone stored'}</p>
                  </div>
                  <div className="booking-contact-actions">
                    {selectedCustomer.phone ? <a href={`tel:${selectedCustomer.phone}`}><Phone size={16} /> Call</a> : null}
                    {selectedCustomer.email ? <a href={`mailto:${selectedCustomer.email}?subject=Autoglow detailing follow-up`}><Mail size={16} /> Email</a> : null}
                  </div>
                </div>

                <div className="customer-detail-stats">
                  <div><span>Total bookings</span><strong>{selectedCustomer.total_bookings ?? selectedBookings.length}</strong></div>
                  <div><span>Last booking</span><strong>{formatDate(selectedCustomer.last_booking_at || previousBooking?.created_at)}</strong></div>
                  <div><span>Next appointment</span><strong>{nextBooking ? formatAppointment(nextBooking.start_at, nextBooking.appointment_timezone ?? undefined) : 'None scheduled'}</strong></div>
                </div>

                <dl className="review-list">
                  <div><dt>Vehicles seen</dt><dd>{customerVehicles(selectedBookings).join(', ') || 'None yet'}</dd></div>
                  <div><dt>Protection used</dt><dd>{customerProtectionUsed(selectedBookings).join(', ') || 'None yet'}</dd></div>
                  <div><dt>Deposit history</dt><dd>{depositSummary(selectedBookings)}</dd></div>
                </dl>

                <section className="customer-reengage-panel">
                  <Sparkles size={18} />
                  <div>
                    <h3>Re-engagement Cue</h3>
                    <p>
                      {isContactable(selectedCustomer)
                        ? `${selectedCustomer.first_name || selectedCustomer.full_name} can be included in future care reminders and seasonal campaigns.`
                        : 'Keep this customer available for manual follow-up until they opt in to marketing email.'}
                    </p>
                  </div>
                </section>

                <section>
                  <h3 className="customer-section-title">Booking History</h3>
                  <div className="booking-run-list customer-history-list">
                    {selectedBookings.length === 0 ? <p className="admin-empty-state">No linked bookings for this customer yet.</p> : null}
                    {selectedBookings.map((booking) => (
                      <article key={booking.id}>
                        <time>{formatDate(booking.start_at || booking.created_at)}<strong>{bookingListStatus(booking)}</strong></time>
                        <div>
                          <h3><a href={adminBookingHref(booking.public_reference)}>{booking.public_reference}</a> · {bookingVehicleLabel(booking)}</h3>
                          <p>{bookingPackage(booking).displayName}</p>
                          <small>{booking.customer_email || selectedCustomer.email} / {booking.customer_phone || selectedCustomer.phone}</small>
                        </div>
                        <span className="status-pill">{booking.payment_status}</span>
                      </article>
                    ))}
                  </div>
                </section>
              </>
            ) : (
              <p className="admin-empty-state">Select a customer to view their details.</p>
            )}
          </article>
        </section>
      </main>
    </div>
  )
}

export default AdminCustomers
